/**
 * Merchant-configured Meta (Facebook) Pixel.
 *
 * Payment pages can carry a merchant's own Pixel ID (`meta_pixel_id`).
 * When present we load fbevents.js once, init the merchant's pixel, and
 * fire standard commerce events (PageView, ViewContent, InitiateCheckout,
 * Purchase) so the merchant's Meta Ads can be optimized on real outcomes.
 *
 * All helpers are safe no-ops on the server or when no pixel was
 * initialized, so callers never need to re-check the pixel ID.
 */

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

// Pixel IDs are numeric (currently 15-16 digits). Reject anything else so a
// malformed value can never reach the fbq bootstrap.
const PIXEL_ID_PATTERN = /^\d{5,20}$/;

const initializedPixels = new Set<string>();

function loadFbq(): Fbq {
  if (window.fbq) return window.fbq;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  return fbq;
}

/**
 * Initialize the merchant's pixel and fire the initial PageView.
 * Safe to call multiple times (e.g. React strict-mode remounts).
 */
export function initMetaPixel(pixelId: string | null | undefined) {
  if (typeof window === "undefined") return;
  if (!pixelId || !PIXEL_ID_PATTERN.test(pixelId)) return;
  if (initializedPixels.has(pixelId)) return;
  initializedPixels.add(pixelId);

  const fbq = loadFbq();
  fbq("init", pixelId);
  fbq("track", "PageView");
}

/**
 * Fire a standard Meta Pixel event. No-op when no pixel is initialized.
 *
 * Pass `eventID` to enable Meta's deduplication: repeated events carrying
 * the same ID (e.g. a Purchase re-fired by overlapping status polls, or
 * matched against a server-side Conversions API event) are counted once.
 */
export function trackMetaEvent(
  event: "ViewContent" | "InitiateCheckout" | "AddPaymentInfo" | "Purchase",
  params?: Record<string, unknown>,
  eventID?: string,
) {
  if (typeof window === "undefined" || !window.fbq) return;
  if (initializedPixels.size === 0) return;
  if (eventID) {
    window.fbq("track", event, params, { eventID });
  } else {
    window.fbq("track", event, params);
  }
}
